import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { GeoDsCoreDataService, GUID, QueryColumn, QueryColumnSortOrder } from '@wissenswerft/core/data';
import { GridComponent, Mode, ToastType } from '@wissenswerft/ww-library';
import { Measure, SubProject } from '@xmt-models';
import { DxButtonComponent, DxContextMenuComponent, DxPopupComponent } from 'devextreme-angular';
import { Column } from 'devextreme/ui/data_grid';
import { Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AppService } from '../../../services/app.service';
import { DataService, ObjectKeys } from '../../../services/data.service';
import { MeasureService } from '../measure.service';
import { DatePipe } from '@angular/common';
import { FavoritesService } from 'libs/favorites/src/lib/favorites.service';
import { ObjectTypeNames } from '../../../favorite-list/favorite-list.component';
import { Person } from '@geods/base';
import { DefaultIcons } from '../measure-detail/implementation-plan/implementation-plan.component';

@Component({
  selector: 'ubt-geods-measure',
  templateUrl: './measure-list.component.html',
  styleUrls: ['./measure-list.component.scss']
})
export class MeasureListComponent implements OnInit, OnDestroy {
  @ViewChild('measureGrid', { static: true }) measureGrid: GridComponent;
  @ViewChild('confirmDeletePopup') confirmDeletePopup: DxPopupComponent;
  @ViewChild('measureCreationForm') measureCreationForm: DxPopupComponent;
  @ViewChild('myMeasuresButton') myMeasuresButton: DxButtonComponent;
  @ViewChild('contextMenu') contextMenu: DxContextMenuComponent;
  public measure: Measure = new Measure(null);
  public measures: Measure[] = [];
  public selectionMode: Mode = Mode.None;
  public measureColumns: QueryColumn[] = [];
  public columnsHeader: Column[] = [];
  public componentName = "measures";
  private currentProfileId: string;
  public fileDate: string;
  public myMeasureTitle: string;
  public showLoader = false;
  public TooltipTarget: string;
  public ToolTipText: string = '';
  public isVisible: boolean = false;
  private readonly VISIBLE_MEASURE_COLUMNS = ['Status', 'Designation', 'ParentId', 'IdRefPersonResponsible', 'StartDate', 'EndDate', 'CurrentGrade'];
  private subscriptions: Subscription[] = [];
  private groupByColumnIndex = [];
  private subProjects: SubProject[] = [];
  public noDataText = "Keine Daten";

  public contextMenuItems: any[] = [
    {
      text: this.dataService.res('Ubt-Measure-Edit-Measure'),
      onClick: (event) => {
        this.openMeasureDetails(event);
      },
    },
    {
      text: this.dataService.res('Ubt-Measure-Delete-Measure'),
      onClick: () => {
        this.openConfirmDialog()
      },
    },
    {
      text: this.dataService.res('Ubt-SubProject-Add-to-favorites'),
      onClick: (e) => {
        this.updateFavorite();
      },
    },
  ];
  constructor(
    public dataService: DataService,
    private coreDataService: GeoDsCoreDataService,
    private appService: AppService,
    private measureService: MeasureService,
    private router: Router,
    private datePipe: DatePipe,
    public favoritesService: FavoritesService
  ) { }

  public ngOnInit(): void {
    this.dataService.floatingButtonConfig();

    this.showLoader = true;
    this.myMeasureTitle = this.dataService.res('Ubt-Measure-My-Measures');
    this.appService.measureIds = [];
    this.dataService.checkGridGroupByColumns('measureGrid', this.groupByColumnIndex);
    this.appService.callMeasureName(undefined);

    this.subscriptions.push(this.dataService.prepareStaffMembers().pipe(switchMap(staffMembers => {
      if (Array.isArray(staffMembers) && staffMembers.length > 0) {
        this.dataService.cachedResponsiblesResponse = staffMembers;
        this.dataService.cachedResponsiblesResponse.sort((a, b) => a.LastName.localeCompare(b.LastName));
      }
      return this.coreDataService.executeReadObjectTypeInfo({ ObjectTypeName: ObjectKeys.MEASURE }).pipe(switchMap(layoutStructure => {
        layoutStructure.Columns.forEach(column => {
          this.columnsHeader.push({
            dataField: column.Name,
            caption: (column.Name === 'ParentId') ? this.dataService.res('Ubt-Measure-List-' + 'SubProject')
              : this.dataService.res('Ubt-Measure-List-' + column.DisplayText),
            dataType: this.dataService.getDataType(column.DataType),
            visibleIndex: this.VISIBLE_MEASURE_COLUMNS.indexOf(column.Name),
            visible: (this.VISIBLE_MEASURE_COLUMNS.includes(column.Name)) ? true : false,
            showInColumnChooser: (!this.appService.CONFIG_PROPERTIES.includes(column.Name)) ? true : false,
            groupIndex: (column.Name === 'ParentId') ? 0 : undefined,
            width: this.getColumnWidth(column.Name),
            format: this.dataService.getDataType(column.DataType) === 'date' ? 'dd.MM.yyyy' : undefined,
            lookup: (this.getLookupColumn(column.Name))
          });
          (column.Name === 'ParentId') ? this.measureColumns.push(this.coreDataService.createQueryColumn('!.Designation', column.Name, QueryColumnSortOrder.None)) :
            this.measureColumns.push(this.coreDataService.createQueryColumn(column.Name, column.Name, QueryColumnSortOrder.None));
        });
        this.measureColumns.push(this.coreDataService.createQueryColumn(
          "Exists(Type(BoxDetail)[Box.ParentId = $CurrentUserId AND Box.Designation = 'Favoriten' AND ParentId = ^.Id])",
          'IsFavorite'
        ), this.coreDataService.createQueryColumn(
          "Type(BoxDetail)[Box.ParentId = $CurrentUserId AND Box.Designation = 'Favoriten' AND ParentId = ^.Id].Id",
          'BoxDetailId'
        ));
        this.columnsHeader.push({
          type: "buttons",
          caption: '',
          alignment: 'left',
          minWidth: 50,
          width: '50px',
          dataField: '',
        });
        return this.prepareProfileId().pipe(switchMap(currentProfile => {
          this.currentProfileId = currentProfile[0] ? currentProfile[0].Id : "";
          return this.measureService.prepareMeasuresByProjectId(this.measureColumns);
        }))
      }))
    })).subscribe((measures) => {
      if (Array.isArray(measures) && measures.length > 0) {
        this.measures = measures;
      } else {
        this.appService.callNotification({ message: this.dataService.res('Ubt-NoData-message'), type: ToastType.INFO });
      }
    }, error => {
      this.appService.callNotification({ message: error, type: ToastType.ERROR });
      this.showLoader = false;
      console.error(error);
    }, () => {
      this.showLoader = false;
    }));

    this.subscriptions.push(this.dataService.updateGridData$.subscribe((measure: Measure) => {
      this.getMeasureByIdAndUpdate(this.measureColumns, measure.Id);
    }));
  }

  public onContentReady(e): void {
    this.appService.measureIds = [];
    const dataSourceFilter = this.measureGrid.dxDataGrid.instance.getCombinedFilter();
    const dataSource = this.measureGrid.dxDataGrid.instance.getDataSource();
    if (e && dataSourceFilter) {
      const filteredData = dataSource.items();
      filteredData.forEach((_filter) => {
        _filter.items.forEach((_item) => {
          this.appService.measureIds.push(_item.Id);
        })
      });
      this.appService.measureIds.filter((value, index, self) => {
        return self.indexOf(value) === index;
      });
    }
  }

  public isFavoriteVisible(e) {
    if (e.row?.data.IsFavorite === 1) {
      return true;
    } else {
      return false;
    }
  }

  private updateFavorite(): void {
    if (this.measure['IsFavorite'] === 1) {
      return this.deleteFavorite();
    } else {
      return this.addFavorite();
    }
  }

  public addFavorite(): void {
    this.favoritesService.persistFavoriten(GUID.newGUID(), ObjectTypeNames.Measure, this.measure.Id, this.measure['Designation']).subscribe((data) => {
      this.measuresUpdateState(this.measure, 'add', data);
    });

  }

  public deleteFavorite(): void {
    this.favoritesService.deleteFavoriten(this.measure['BoxDetailId']);
    this.measuresUpdateState(this.measure, 'delete')
  }

  public measuresUpdateState(e, state: string, favoriteData?): void {
    let selectedIndex = this.measures.findIndex((measure) => {
      return measure.Id === e.Id
    });

    if (state === 'add') {
      const clonedItem = { ...e, IsFavorite: 1, BoxDetailId: favoriteData.Id };
      this.measures[selectedIndex]
      this.measures[selectedIndex] = clonedItem;
    } else {
      const clonedItem = { ...e, IsFavorite: 0 };
      this.measures[selectedIndex] = clonedItem;
    }
  }
  public measureCellTemplate = (e) => {
    if (e.column.dataField === 'Status' && e.rowType === 'data') {
      e.cellElement.style.textAlign = "center";
      if (e.data.Status === this.measureService.measureStatus[0].name) {
        return e.cellElement.innerHTML = MeasureStatusIcons.Done;
      } else if (e.data.Status === this.measureService.measureStatus[1].name) {
        return e.cellElement.innerHTML = MeasureStatusIcons.InProgress;
      } else {
        return e.cellElement.innerHTML = MeasureStatusIcons.Offen;
      }
    }
    if (e.rowType === "data") {
      e.cellElement.classList.add("myClass");
    }
  }

  public hoverTooltip(event): void {
    if (event.rowType === 'data' && event.column.dataField === 'Status') {
      this.TooltipTarget = event.cellElement;
      if (event.eventType === 'mouseover') {
        event.value ? this.isVisible = true : this.isVisible = false;
        this.ToolTipText = event.value;
      } else {
        this.isVisible = false;
      }
    }
  }
  public onCellPrepared = (e) => {
    if (e.rowType === 'data') {
      if (e.column.dataField === 'Status') {
        e.cellElement.style.textAlign = 'center';
      } else if (e.column.dataField === '') {
        e.cellElement.style.cursor = 'pointer';
        return (e.cellElement.innerHTML = DefaultIcons.CONTEXTMENU);
      }
    }
  }

  private getColumnWidth(key: string): string {
    if (key === 'Designation') {
      return '40%';
    } else if (key === 'Status') {
      return '10%';
    } else {
      return 'auto';
    }
  }

  private prepareProfileId(): Observable<Person[]> {
    const profileColumns = [
      this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None)
    ];
    const profileOPath = 'Login=' + "'" + localStorage.getItem('userLogin') + "'";
    return this.dataService.readObjects<Person[]>(ObjectKeys.PERSON, profileColumns, profileOPath)
  }

  public loadMyMeasures() {
    if (this.myMeasureTitle === this.dataService.res('Ubt-Measure-My-Measures')) {
      const myMeasures = this.measures.filter(measure => { return measure['IdRefPersonResponsible'] === this.currentProfileId; });
      if (Array.isArray(myMeasures) && myMeasures.length > 0) {
        this.measureGrid.dxDataGrid.dataSource = myMeasures;
        this.myMeasureTitle = this.dataService.res('Ubt-Measure-All-Measures');
      } else {
        this.appService.callNotification({ message: this.dataService.res('Ubt-Measure-No-Measure-Assgined'), type: ToastType.INFO });
      }
    } else {
      this.measureGrid.dxDataGrid.dataSource = this.measures;
      this.myMeasureTitle = this.dataService.res('Ubt-Measure-My-Measures');
    }

  }

  private getLookupColumn(key: string) {
    const resposibles = this.dataService.cachedResponsiblesResponse;
    if ((key === 'IdRefPersonResponsible') || (key === 'IdRefPersonConsultant')) {
      return {
        valueExpr: 'Id', displayExpr: this.dataService.getResponsibleFullName, dataSource: resposibles
      };
    } if (key === "Status") {
      return { valueExpr: 'name', displayExpr: 'value', dataSource: this.measureService.measureStatus };
    }
  }

  private getMeasureByIdAndUpdate(columns: QueryColumn[], id: string) {
    const opath = 'Id=' + "'" + id + "'";
    this.subscriptions.push(this.dataService.readObjects(ObjectKeys.MEASURE, columns, opath).subscribe((data) => {
      this.measures.push(data[0]);
      this.measureGrid.refreshGrid();
      this.appService.callNotification({ message: this.dataService.res('Ubt-Notification-Success'), type: ToastType.SUCCESS });
    }, error => {
      this.appService.callNotification({ message: error, type: ToastType.ERROR });
      console.error(error);
    }));
  }

  public openMeasureDetails(event?): void {
    // if (!event) this.measureService.selectedMeasures = this.selectedMeasures;
    this.measureService.currentMeasureIndex = this.appService.measureIds.findIndex(item => item === this.measure.Id);
    // this.dataService.sendMeasureDetail(measureDetail);
    this.dataService.appService.showMeasureIcon = true;
    this.router.navigate(['measureDetail', 'measures', this.measure.Id]);
  }

  public exporting(event): void {
    const myDate = this.datePipe.transform(new Date(), '/yyyy-MM-dd');
    this.fileDate = myDate;
  }

  public onCellClick = (e) => {
    this.measure = e.data;
    if (e.columnIndex !== 7 && e.rowType === "data") {
      this.router.navigate(['measureDetail', 'measures', e.data.Id]);
    } else if (e.columnIndex === 7) {
      e.cellElement.className = 'context-menu-icon' + e.rowIndex;
      this.contextMenu.target = '.context-menu-icon' + e.rowIndex;
      e.cellElement.innerHTML = this.contextMenu.visible ? DefaultIcons.CLOSE : DefaultIcons.CONTEXTMENU;
      this.contextMenu.instance.show();
    }
  }

  public openCreationPopup(): void {
    this.measureCreationForm.instance.show();
  }

  public closeCreationPopup(): void {
    this.measureCreationForm.instance.hide();
  }

  private openConfirmDialog(): void {
    this.confirmDeletePopup.instance.show();
  }

  public removeMeasure(): void {
    this.subscriptions.push(this.dataService.deleteObject(ObjectKeys.MEASURE, this.measure.Id).subscribe(() => {
      this.appService.callNotification({ message: this.dataService.res("Ubt-Notification-Delete"), type: ToastType.INFO });
      this.confirmDeletePopup.instance.hide();
      const index = this.measures.findIndex(item => item.Id === this.measure.Id);
      this.measures.splice(index, 1);
      this.measureGrid.refreshGrid();
    }, error => {
      console.error(error)
      this.appService.callNotification({ message: error, type: ToastType.ERROR });
    }));
  }

  public abortDelete(): void {
    this.confirmDeletePopup.instance.hide();
  }

  public ngOnDestroy(): void {
    this.subscriptions.map(subscription => subscription.unsubscribe());
  }
}

export enum MeasureStatusIcons {
  Done = '<svg height="27px" id="uuid-04f03c21-7a02-4875-beab-459bdab292ff" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><defs><style>.uuid-3799ce17-e82a-4e82-b177-6f162b86d123{fill:#228B22;}</style></defs><path class="uuid-3799ce17-e82a-4e82-b177-6f162b86d123" d="M60,12c-26.47,0-48,21.53-48,48s21.53,48,48,48,48-21.53,48-48S86.47,12,60,12Zm0,90c-23.16,0-42-18.84-42-42S36.84,18,60,18s42,18.84,42,42-18.84,42-42,42Z"/><polygon class="uuid-3799ce17-e82a-4e82-b177-6f162b86d123" points="50 72.77 38.09 60.87 33.85 65.11 50 81.26 86.15 45.11 81.91 40.86 50 72.77"/></svg>',
  InProgress = '<svg height="27px"  id="uuid-37a4816a-728e-4fd5-83b2-70d733623b3e" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><defs><style>.uuid-45f68346-afdf-4317-98a5-c65e1da98155{fill:	#00569c;}</style></defs><path class="uuid-45f68346-afdf-4317-98a5-c65e1da98155" d="M84.15,50.93c.24-1.35,.27-2.74,.07-4.13l4.25-1.76v-.04c-.21-1.05-.52-2.08-.94-3.09-.36-.88-.81-1.69-1.28-2.47l-4.21,1.75c-.83-1.19-1.85-2.21-2.98-3.04l1.76-4.25c-1.69-1.08-3.57-1.87-5.53-2.32l-1.75,4.23c-1.41-.24-2.86-.25-4.32,0l-1.75-4.22c-.94,.22-1.87,.49-2.8,.88-.97,.4-1.87,.89-2.73,1.44l1.76,4.24c-1.17,.85-2.18,1.89-2.99,3.06l-4.22-1.75c-1.05,1.71-1.82,3.59-2.24,5.56l4.26,1.76c-.2,1.36-.19,2.75,.06,4.14l-4.21,1.75c.21,.9,.48,1.78,.84,2.66,.42,1.02,.93,1.96,1.51,2.85l4.25-1.76c.82,1.09,1.78,2.03,2.87,2.8l-1.75,4.23c1.71,1.07,3.59,1.84,5.55,2.27l1.76-4.25c1.29,.2,2.63,.21,3.95,0l1.76,4.24c.99-.22,1.99-.51,2.96-.91,.91-.38,1.77-.84,2.59-1.35l-1.75-4.22c1.11-.8,2.07-1.74,2.87-2.8l4.26,1.76c1.1-1.69,1.88-3.56,2.35-5.52l-4.22-1.75Zm-10.11,4.13c-3.56,1.47-7.66-.23-9.13-3.78s.23-7.66,3.78-9.13,7.66,.23,9.13,3.78-.23,7.66-3.78,9.13Z"/><path class="uuid-45f68346-afdf-4317-98a5-c65e1da98155" d="M108,60c0-26.47-21.53-48-48-48-8.46,0-16.41,2.21-23.32,6.07l4.23,4.54c5.73-2.94,12.22-4.61,19.09-4.61,23.16,0,42,18.84,42,42,0,8.97-2.84,17.27-7.64,24.1l-4.51-4.91-2.6,16.45,16.61-1.21-5.35-5.82c5.96-8,9.5-17.89,9.5-28.6Z"/><path class="uuid-45f68346-afdf-4317-98a5-c65e1da98155" d="M60,102c-1.33,0-2.64-.07-3.93-.19,.43-.8,.82-1.62,1.17-2.46,1.04-2.5,1.69-5.1,1.98-7.72h7.02c.17-1.98,.18-3.99,0-6h-7.03c-.29-2.62-.94-5.21-1.98-7.72-1.05-2.53-2.43-4.82-4.07-6.84l4.98-4.98c-1.3-1.55-2.73-2.96-4.25-4.24l-4.98,4.98c-4.17-3.35-9.24-5.45-14.56-6.03v-7.04c-1.98-.17-3.99-.18-6,0v7.04c-2.59,.28-5.19,.92-7.72,1.97-.85,.35-1.67,.75-2.46,1.17-.12-1.3-.19-2.61-.19-3.94,0-8.97,2.83-17.28,7.64-24.1l4.52,4.92,2.61-16.45-16.61,1.21,5.35,5.82c-5.96,8-9.5,17.89-9.5,28.61,0,26.47,21.53,48,48,48,8.46,0,16.41-2.21,23.32-6.07l-4.23-4.54c-5.73,2.94-12.22,4.61-19.09,4.61Zm-40.73-31.73c1.14-.75,2.36-1.42,3.68-1.96,2.74-1.14,5.59-1.67,8.39-1.67,8.64,0,16.85,5.12,20.35,13.58,2.25,5.43,2.25,11.41,0,16.84-.54,1.3-1.21,2.52-1.97,3.67-14.92-3.77-26.68-15.53-30.45-30.45Z"/></svg>',
  Offen = '<svg  height="27px"  id="uuid-c532442b-353e-4cb6-89eb-f2551873f941" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><defs><style>.uuid-9fad3273-f016-4e9d-a232-5afba1bcffac{fill:	#FF6347;}</style></defs><path class="uuid-9fad3273-f016-4e9d-a232-5afba1bcffac" d="M63.03,63l-15.42,15.42,4.24,4.24,22.66-22.66-22.66-22.66-4.24,4.24,15.42,15.42H12.1c-.06,.99-.1,1.99-.1,3s.04,2.01,.1,3H63.03Z"/><path class="uuid-9fad3273-f016-4e9d-a232-5afba1bcffac" d="M60,12c-21.38,0-39.53,14.05-45.72,33.39h6.35c5.94-15.98,21.34-27.39,39.37-27.39,23.16,0,42,18.84,42,42s-18.84,42-42,42c-18.02,0-33.42-11.42-39.37-27.39h-6.35c6.19,19.35,24.34,33.39,45.72,33.39,26.47,0,48-21.53,48-48S86.47,12,60,12Z"/></svg>'
}
